/* eslint-disable no-console */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { ArrowBackIos, Add } from '@material-ui/icons';
import styled from 'styled-components';
import { TextField, useForm } from '../components/FormInputs';
import { Normal } from '../components/Button';

// Utils
import Mixpanel from '../../utils/mixpanel';

// Styles
import { colors, padding, fs, radius } from '../styles/variables';

const Scene = styled.div`
  position: relative;
  width: 23.6rem;
  min-width: 23.6rem;
  margin: ${padding.v};
  perspective: 600px;
  height: 20rem;
`;

const Card = styled.div`
  width: 100%;
  height: 100%;
  transition: transform 1s;
  transform-style: preserve-3d;
  position: relative;

  &.flipped {
    transform: rotateY(-180deg);
  }

  .card__face {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    backface-visibility: hidden;
    display: flex;
    align-items: end;
    justify-content: center;
    border-radius: ${radius.default};
    color: ${colors.white};
  }

  .front {
    cursor: pointer;
    background-color: ${colors.blue.navy};
  }

  .back {
    flex-direction: column;
    background-color: ${colors.blue.navy};
    transform: rotateY(180deg);
    align-items: center;
    justify-content: space-evenly;
    #createStoryboard {
      padding: 1.5rem 1.5rem 1.8rem 1.5rem;
    }
  }
`;

const BackButton = styled.button`
  width: 100%;
  cursor: pointer;
  background-color: transparent;
  color: ${colors.white};
  font-size: ${fs.xs};
  display: flex;
  align-items: center;
  padding: 0 ${padding.h};
  border: none;
  &:focus {
    outline: none;
  }
`;

const CreateButton = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;
  color: ${colors.blue.primary};
  &:hover {
    .create-icon {
      transform: rotate(90deg);
    }
  }
  .create-icon {
    align-items: center;
    margin-bottom: 1.5rem;
    width: 3.6rem;
    height: 3.6rem;
    border: 0.1rem solid #2c86ff;
    border-radius: 100%;
    display: flex;
    justify-content: center;
    transition: all 0.2s ease;
    .MuiSvgIcon-root {
      font-size: 2.4rem;
      fill: ${colors.blue.primary};
    }
  }
  .create-text {
    font-size: 1.6rem;
  }
`;

const StoryboardName = styled.div`
  padding: 0 1.5rem;
  width: 100%;
  .MuiTextField-root {
    .MuiInputLabel-outlined {
      font-size: 1.5rem;
      color: #fff;
      transform: translate(1.4rem, 2rem) scale(1);
      &.Mui-error {
        color: #ff6c56;
      }
      &.MuiInputLabel-shrink {
        transform: translate(1.24rem, -0.4rem) scale(0.65);
        &.Mui-error {
          color: #ff6c56;
        }
      }
    }
    .MuiOutlinedInput-root {
      border-radius: 0.4rem;
      &:hover {
        .MuiOutlinedInput-notchedOutline {
          border-color: #fff;
        }
      }
      &.Mui-error {
        border-color: #ff6c56;
      }
      .MuiInputBase-input {
        color: #fff;
        font-size: 1.4rem;
      }
    }
    .Mui-focused {
      .MuiOutlinedInput-notchedOutline {
        border-color: #fff;
      }
    }
    .MuiOutlinedInput-notchedOutline {
      border-color: #fff;
      padding-left: 0.8rem;
      &.Mui-error {
        border-color: #ff6c56;
      }
    }
  }
`;

const INITIAL_STATE = {
  createProject: {
    // initial state to set using id of input field eg. brief is passed as "id" to TextField
    value: '', // example to set default value from props
    error: undefined,
    validation: value => {
      if (!value) {
        return 'This field is required';
      }
      if (value.length > 30) {
        return 'Limit is of 30 characters';
      }
      return undefined;
    },
    required: true,
  },
};

const CreateProject = props => {
  const { createStoryboard, history, toggleUpgradeModal, toggleToast } = props;
  const [isSubmitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState(false);
  const { formValues, onChange, onSubmit, reset } = useForm(
    INITIAL_STATE,
    async () => {
      setSubmitting(true);
      Mixpanel.track('Create a Project');
      const createResponse = await createStoryboard({
        data: {
          title: formValues.createProject.value,
          brief: '',
        },
      });

      setSubmitting(false);

      const {
        data: { response, data, errormessage },
      } = createResponse;

      if (response) {
        history.push(`/project/${data.project_db_id}`);
      } else if (data?.upgrade_required || data?.access_denied) {
        toggleUpgradeModal({
          show: true,
          data,
          event: {
            description: 'Create 2nd project',
          },
        });
      } else {
        toggleToast({
          message: {
            messageHead: 'Error',
            messageBody: errormessage,
            variant: 'error',
          },
        });
      }
    }
  );

  return (
    <Scene className='grid-wrapper'>
      <Card className={form ? 'grid-int-wrapper flipped' : 'grid-int-wrapper'}>
        <div
          className='card__face front'
          role='presentation'
          onClick={() => setForm(true)}
        >
          <CreateButton>
            <div className='create-icon'>
              <Add />
            </div>
            <span className='create-text'>Create a Project</span>
          </CreateButton>
        </div>

        <div className='card__face back'>
          <BackButton
            type='button'
            onClick={() => {
              setForm(false);
              setError(false);
              reset();
            }}
          >
            <ArrowBackIos />
            <span className='create-text'>Back</span>
          </BackButton>

          <StoryboardName>
            <TextField
              autoComplete='off'
              id='createProject'
              label='Name'
              textboxtype='outlined'
              value={formValues.createProject.value}
              onChange={e => {
                setError(false); // clear api error
                onChange(e);
              }}
              error={!!formValues.createProject.error}
              helperText={formValues.createProject.error}
              autoFocus
            />
          </StoryboardName>

          {/* {error && (
            <div style={{ padding: '0 1.5rem' }}>
              <Label style={{ paddingTop: '0' }} color={colors.error}>
                {error}
              </Label>
            </div>
          )} */}

          <div
            className='h-padd'
            style={{ width: '100%', textAlign: 'center' }}
          >
            <Normal isLoading={isSubmitting} onClick={onSubmit}>
              Create
            </Normal>
          </div>
        </div>
      </Card>
    </Scene>
  );
};

CreateProject.propTypes = {
  createStoryboard: PropTypes.func,
  history: PropTypes.instanceOf(Object),
  toggleUpgradeModal: PropTypes.func,
  toggleToast: PropTypes.func,
};

export default CreateProject;
